'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

/**
 * The tooltip bubble the Univer sheet toolbar draws (rounded-lg inverted
 * surface with a rotated-square caret, instant show), ported onto app
 * tokens so every editor tooltip matches the sheet editor. Shared with
 * ToolbarHoverTips, which renders the same surface outside Radix.
 */
// text-pretty, not text-balance: balance shrinks the box to a fractionally
// rounded width, which clips the last glyph of single-line tooltips.
export const tooltipSurfaceClass =
  'bg-foreground text-background w-fit max-w-sm rounded-md px-2 py-1.5 text-base font-medium text-pretty shadow-floating';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

// Radix adds the measured arrow height (10px) to sideOffset, so -2 nets the
// 8px trigger gap the Univer tooltip uses; keeping the real Arrow (vs a
// static caret) means Radix pins it to the trigger when the bubble shifts
// away from a viewport edge.
function TooltipContent({
  className,
  sideOffset = -2,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          tooltipSurfaceClass,
          // `group`: the arrow reads the content's data-side to hide itself
          // on horizontal placements.
          'group animate-in fade-in-0 zoom-in-95 z-50 origin-(--radix-tooltip-content-transform-origin)',
          className
        )}
        {...props}
      >
        {children}
        {/* The tuck translate is tuned for top/bottom placement; on left/right
            Radix rotates the frame and the same translate parks the square
            over the bubble's text — hide the caret on horizontal sides. */}
        {/* `hidden!`: the svg ships inline display:block from Radix, so the
            horizontal-side hide needs !important to win. */}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_4px)] rotate-45 rounded-xs group-data-[side=left]:hidden! group-data-[side=right]:hidden!" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

function TooltipShortcut({ className, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn(
        'ml-2 border border-background/25 px-1 py-0.5 font-mono text-xs',
        className
      )}
      {...props}
    />
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipShortcut,
};
